/**
 * Api_Endpoints.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for every backend URL used in the SK Voice Console.
 *
 * Two backend services:
 *   PLATFORM_API  → skplatform-api   (auth & identity — login / refresh / me;
 *                   issues the httpOnly cookies directly)
 *   VOICE_API     → sk-orchestrator  (voice-setup / voice-controls /
 *                   voice-console read+write surfaces + live-calls SSE)
 *
 * Auth/identity call skplatform-api DIRECTLY (:3013 in local dev), matching
 * SK Catalog: the earlier backbone indirection only proxied the platform's
 * contract byte-for-byte, adding a hard dependency for no benefit.
 *
 * Rules:
 *   - Zero hardcoded URLs anywhere else in the codebase.
 *   - To switch environments, update ONLY the base URLs below.
 * ──────────────────────────────────────────────────────────────────────────
 */

// Resolve a backend base URL. Build-time env (webpack DefinePlugin) wins so the
// deployment pipeline can override; when it isn't supplied we derive the host
// from window.location so dev/staging/prod work without a build-arg — same
// approach as platformAuthRedirect.ts. `localhost` picks the local dev port.
// (Interim hardcode: the sk-voice-frontend dev pipeline/build-args don't exist
// yet, so this keeps voice.dev.softknack.com pointing at the right backends.)
function resolveApiBase(envUrl: string | undefined, localPort: string, host: (env: 'dev' | 'staging' | 'prod') => string): string {
  if (envUrl) return envUrl;
  if (typeof window === 'undefined') return host('prod');
  const h = window.location.hostname;
  if (/^(localhost|127\.0\.0\.1)$/.test(h)) return `http://localhost:${localPort}`;
  if (h.includes('.dev.')) return host('dev');
  if (h.includes('.staging.')) return host('staging');
  return host('prod');
}

// ── PLATFORM API base (auth, identity — skplatform-api, owns /v1/me) ───────
export const PLATFORM_API = resolveApiBase(
  process.env.PLATFORM_API_URL,
  '3000',
  (env) => env === 'prod' ? 'https://platform.api.softknack.com' : `https://platform.${env}.api.softknack.com`,
);

// ── VOICE API base (sk-orchestrator — voice data + live SSE) ──────────────
// Orchestrator (the voice BFF) is deployed at suki.api.<env>.softknack.com.
export const VOICE_API = resolveApiBase(
  process.env.VOICE_API_URL,
  '3013',
  (env) => env === 'prod' ? 'https://suki.api.softknack.com' : `https://suki.api.${env}.softknack.com`,
);

// ── Versioned prefixes ─────────────────────────────────────────────────────
const P = `${PLATFORM_API}/v1`; // Auth/identity — skplatform-api v1 (direct)
const V = `${VOICE_API}/v1`;    // Voice — sk-orchestrator v1

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM API — Auth & Identity  (httpOnly cookie session; product = 'voice')
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Signup_Url = `${P}/auth/signup`;             // POST { …, product:'voice' }
export const Platform_Login_Url = `${P}/auth/login`;              // POST { email, password }
export const Platform_Login_Mfa_Url = `${P}/auth/login/mfa`;          // POST { mfa_token, code }
export const Platform_Refresh_Token_Url = `${P}/auth/refresh`;            // POST — silent rotation
export const Platform_Logout_Url = `${P}/auth/logout`;             // POST — clears session cookies (204)
export const Platform_Me_Url = `${P}/me`;                      // GET  — identity probe
export const Platform_Me_Setup_Url = `${P}/me/setup`;         // PATCH — persist onboarding progress
export const Platform_Verify_Email_Url = `${P}/auth/verify-email`;       // POST { token }
export const Platform_Resend_Verification_Url = `${P}/auth/resend-verification`; // POST { product:'voice' }
export const Platform_Switch_Org_Url = `${P}/auth/switch-org`;         // POST { org_id }
// Platform-admin org list (SSOT for orgs) — the "Configuration for" org picker.
// Identity route → platform-direct (like /v1/me), NOT proxied through voice.
export const Platform_Admin_Orgs_Url = `${P}/me/admin/orgs`;           // GET — platform_admin only; {orgs|items:[{id,name}]}

/* ═══════════════════════════════════════════════════════════════════════════
   VOICE — Setup tier (Softknack / platform_admin — global)
   Intent catalog + editor: create / enrichment / versions / rollback.
═══════════════════════════════════════════════════════════════════════════ */

export const Voice_Setup_Intents_Url = `${V}/voice-setup/intents`;                                   // GET (list w/ counts) | POST (create)
export const Voice_Setup_McpTools_Url = `${V}/voice-setup/mcp-tools`;                                 // GET — real MCP registry {name,role} for the picker
export const Voice_Setup_Intent_Url = (id: string) => `${V}/voice-setup/intents/${id}`;             // GET (detail: bindings+versions+utterances)
export const Voice_Setup_Intent_Enrichment_Url = (id: string) => `${V}/voice-setup/intents/${id}/enrichment`;      // POST — add/toggle enrichment binding
export const Voice_Setup_Intent_Versions_Url = (id: string) => `${V}/voice-setup/intents/${id}/versions`;        // POST — cut + set current
export const Voice_Setup_Intent_CurrentVersion_Url = (id: string) => `${V}/voice-setup/intents/${id}/current-version`; // PUT — rollback / set current

// Router prompt — one global versioned orchestration prompt (Softknack).
export const Voice_Setup_Router_Prompt_Url = `${V}/voice-setup/router-prompt`;                 // GET (versions+current) | POST (cut new + set current)
export const Voice_Setup_Router_Prompt_Current_Url = `${V}/voice-setup/router-prompt/current`; // PUT { versionNo } — set an existing version current

// Phone & KYC (Softknack staff) — give an org a KYC Plivo already accepted, so it never files one again.
export const Voice_Admin_Plivo_Kycs_Url = `${V}/admin/plivo/kycs`;                                   // GET — every KYC on the Plivo account
export const Voice_Admin_Org_Number_Url = (orgId: string) => `${V}/admin/orgs/${orgId}/number`;         // GET — the org's go-live journey
export const Voice_Admin_Org_Kyc_Link_Url = (orgId: string) => `${V}/admin/orgs/${orgId}/number/kyc-link`; // POST { compliance_id }

// Provider & pipeline config — platform default + per-org overrides (Softknack).
export const Voice_Setup_Providers_Url = `${V}/voice-setup/providers`;                                     // GET (default + overrides)
export const Voice_Setup_Providers_Default_Url = `${V}/voice-setup/providers/default`;                     // PUT — save the platform default (cuts a version)
export const Voice_Setup_Providers_Override_Url = (orgId: string) => `${V}/voice-setup/providers/override/${orgId}`; // PUT (save override) | DELETE (reset to inherit)

/* ═══════════════════════════════════════════════════════════════════════════
   VOICE — Controls tier (Operator / org admin)
   Intent toggles (403 INTENT_LOCKED on unlicensed) + enrichment sub-toggles.
═══════════════════════════════════════════════════════════════════════════ */

export const Voice_Controls_Intents_Url = `${V}/voice-controls/intents`;      // GET (list w/ Live/Locked/Off state) | PUT { intentId, enabled }

// ── Number provisioning (org-scoped, sk-orchestrator) ──
export const Org_Locations_Url = (orgId: string) => `${V}/orgs/${orgId}/locations`;                  // GET
export const Org_Phone_Numbers_Url = (orgId: string) => `${V}/orgs/${orgId}/phone-numbers`;          // GET
export const Org_Available_Numbers_Url = (orgId: string) => `${V}/orgs/${orgId}/plivo/available-numbers`; // GET ?country&type&pattern
export const Org_Claim_Franchise_Url = (orgId: string) => `${V}/orgs/${orgId}/phone-number`;         // POST { numberSid } — org-level
export const Org_Claim_Branch_Url = (orgId: string, locId: string) => `${V}/orgs/${orgId}/locations/${locId}/phone-number`; // POST { numberSid }

// ── Go-live provisioning journey (subaccount → KYC → buy → live) ──
export const Prov_State_Url = (orgId: string) => `${V}/orgs/${orgId}/provisioning`;             // GET
export const Prov_Start_Url = (orgId: string) => `${V}/orgs/${orgId}/provisioning/start`;       // POST { location_id? }
export const Prov_Kyc_Url = (orgId: string) => `${V}/orgs/${orgId}/provisioning/kyc`;           // POST submit | GET poll
export const Prov_Numbers_Url = (orgId: string) => `${V}/orgs/${orgId}/provisioning/numbers`;   // GET ?pattern
export const Prov_Buy_Url = (orgId: string) => `${V}/orgs/${orgId}/provisioning/buy`;           // POST { number, location_id? }
export const Voice_Controls_Enrichment_Url = `${V}/voice-controls/enrichment`; // PUT { intentId, toolName, on }

/* ═══════════════════════════════════════════════════════════════════════════
   VOICE — Console tier (Operator write / Viewer read)
   Follow-ups. (Plan and billing are Platform's plan card, not a Voice endpoint.)
═══════════════════════════════════════════════════════════════════════════ */

export const Voice_Console_Followups_Url = `${V}/voice-console/followups`;                       // GET ?status=OPEN|RESOLVED|DISMISSED
export const Voice_Console_Followup_Url = (id: string) => `${V}/voice-console/followups/${id}`;  // PATCH { status, assignee }

// Agent settings — persona / hours / escalation / csat / notifications / compliance (Operator).
export const Voice_Controls_Settings_Url = `${V}/voice-controls/settings`; // GET | PUT (partial patch)

/* ═══════════════════════════════════════════════════════════════════════════
   VOICE — Live calls (SSE; Redis pub/sub, org-scoped, 25s heartbeat)
═══════════════════════════════════════════════════════════════════════════ */

export const Voice_Live_Calls_Url = `${V}/live/calls`; // GET (text/event-stream)

/* ═══════════════════════════════════════════════════════════════════════════
   VOICE — Call data (frontend read API; org-scoped, JWT)
   Call log list + KPI strip + per-call insights + analytics console.
═══════════════════════════════════════════════════════════════════════════ */

export const Voice_Calls_Url = `${V}/calls`;                                              // GET ?status&from&to&caller&limit&cursor → { data, nextCursor }
export const Voice_Calls_Page_Url = `${V}/calls/page`;                                    // GET ?status&caller&from&to&page&pageSize → { data, total, page, pageSize, totalPages }
export const Voice_Outbound_Contacts_Url = `${V}/outbound/contacts`;                        // GET ?q= (search name/phone) · POST {name,phone} (create; 409 on dup phone)
export const Voice_Call_Url = (id: string) => `${V}/calls/${id}`;                          // GET — frontend-read call row (caller name/number, direction, disposition)
export const Voice_Call_Insights_Url = (id: string) => `${V}/calls/${id}/insights`;       // GET — call-detail hero (transcript+ledgers+pins)
export const Voice_Calls_Log_Summary_Url = `${V}/analytics/calls/log-summary`;            // GET — call-log KPI strip
export const Voice_Analytics_Console_Url = `${V}/analytics/calls/console`;                // GET — analytics KPI tiles + outcome distribution

/* ═══════════════════════════════════════════════════════════════════════════
   VOICE — Health
═══════════════════════════════════════════════════════════════════════════ */

// Live Dashboard (board) — org-scoped header identity + today KPI rollup.
export const Voice_Board_Identity_Url = `${V}/board/identity`; // GET — { orgName, dialNumber, vertical, tag, accent }
export const Voice_Board_Summary_Url = `${V}/board/summary`;   // GET — { callsHandled, actionsCompleted, revenue(stub), metrics{} }

export const Voice_Health_Url = `${VOICE_API}/v1/health`; // GET — unauthenticated health check

/* ═══════════════════════════════════════════════════════════════════════════
   VOICE — Outbound capability (SKVOICE-OUTBOUND-001)
   Outgoing ledger, preview, settings, reasons, campaigns, DND, inbound rules.
   All org-scoped (JWT + RLS); reasons are Softknack-only.
═══════════════════════════════════════════════════════════════════════════ */

export const Voice_Outbound_Intents_Url = `${V}/outbound/intents`;                              // GET (list ?status&reason_code&customer_id&limit) | POST (manual create)
export const Voice_Outbound_Intent_Url = (id: string) => `${V}/outbound/intents/${id}`;         // GET — detail + events + attempts
export const Voice_Outbound_Intent_Preview_Url = `${V}/outbound/intents/preview`;               // POST — engine preview, no persist
export const Voice_Outbound_Settings_Url = `${V}/outbound/settings`;                            // GET | PUT (cap/concurrency/lead; quiet hours read-only)
export const Voice_Outbound_ReasonWindows_Url = `${V}/outbound/reason-windows`;                 // PUT (per-reason preferred windows)
export const Voice_Outbound_RetryRules_Url = `${V}/outbound/retry-rules`;                       // PUT (retry matrix per outcome, fanned across reasons)
export const Voice_Outbound_Reasons_Url = `${V}/outbound/reasons`;                              // GET (active) | POST (Softknack)
export const Voice_Outbound_Reason_Url = (code: string) => `${V}/outbound/reasons/${code}`;     // PATCH (Softknack; class immutable)
export const Voice_Campaigns_Url = `${V}/campaigns`;                                            // GET | POST
export const Voice_Campaign_Url = (id: string) => `${V}/campaigns/${id}`;                       // GET — campaign + contacts
export const Voice_Campaign_Launch_Url = (id: string) => `${V}/campaigns/${id}/launch`;         // POST — fan to intents
export const Voice_Dnd_Url = `${V}/dnd`;                                                        // GET ?direction | POST (block)
export const Voice_Dnd_Lift_Url = (id: string) => `${V}/dnd/${id}/lift`;                        // POST — lift a block
export const Voice_Inbound_Settings_Url = `${V}/inbound/settings`;                              // GET | PUT
